<script>
import { required, minLength, maxLength, email } from "@vuelidate/validators";
import {
  tel,
  zipCode,
  password,
  telMobile,
  dateIsMajeur,
  birthdate
} from "@/utils/form-validators";
import { isCarteCora } from "@/utils/fidelite";
import BanniereInformation from "@/components/molecules/banniere-information-compte-carrefour";
import Button from "@/components/atoms/button";
import LazyImage from "@/components/atoms/lazy-image";
import FormStep from "@/components/molecules/form-step";
import Form from "@/components/molecules/form";
import FormGroup from "@/components/molecules/form-group";
import FormInput from "@/components/molecules/form-input";
import FormRadio from "@/components/molecules/form-radio";
import FormCheckbox from "@/components/molecules/form-checkbox";
import FormSelect from "@/components/molecules/form-select";
import FormSelectOption from "@/components/molecules/form-select/form-select-option";
import FormSelectOptGroup from "@/components/molecules/form-select/form-select-optgroup";
import FormTip from "@/components/molecules/form-tip/index";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";
import CarteFideliteTooltip from "@/components/organisms/carte-fidelite-form/tooltip";
import CarteFideliteRetrouverPorteur from "@/components/organisms/carte-fidelite-form/retrouver-porteur";
import MeaCoraproLogin from "@/components/organisms/mise-en-avant-corapro/login";
import GridRow from "@/components/molecules/grid/grid-row";
import GridCol from "@/components/molecules/grid/grid-col";
import Captcha from "@/components/molecules/captcha";
import { getOffset } from "@/utils/focus";
import CmsFetcher from "@/containers/cms-fetcher";
import Popin from "@/containers/popin-adaptive";
import MentionsLegalesCompte from "@/components/molecules/mentions-legales/compte";
import MentionsLegalesNewsletter from "@/components/molecules/mentions-legales/newsletter";
import MentionsLegalesFidelite from "@/components/molecules/mentions-legales/fidelite";
import PasswordRequirements from "@/components/organisms/inscription-form/password-requirements";
import LoadingSpinner from "@/components/molecules/loading-spinner";

import carteMalin from "@/assets/images/carte-malin.png";
import carteCora from "@/assets/images/carte-cora.png";
import illuEurocora from "@/assets/images/eurocora.png";
import avantageMalin from "@/assets/images/avantage-malin.png";

export default {
  name: "InscriptionForm",
  components: {
    Form,
    FormStep,
    FormSelect,
    FormGroup,
    BanniereInformation,
    FormInput,
    FormRadio,
    FormCheckbox,
    Button,
    LazyImage,
    FormSelectOption,
    FormSelectOptGroup,
    FormTip,
    CarteFideliteTooltip,
    CarteFideliteRetrouverPorteur,
    Captcha,
    CmsFetcher,
    Popin,
    MentionsLegalesCompte,
    MentionsLegalesNewsletter,
    MentionsLegalesFidelite,
    PasswordRequirements,
    MeaCoraproLogin,
    GridRow,
    GridCol,
    LoadingSpinner
  },
  props: {
    magasins: {
      type: Array,
      required: true
    },
    magasinId: {
      type: Number,
      required: false,
      default: null
    },
    magasinsByCp: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    isInTunnel: {
      // permet une distinction inscription classique vs depuis le tunnel
      type: Boolean,
      required: false,
      default: false
    },
    isEligibleDefis: {
      type: Boolean,
      required: false,
      default: false
    },
    captchaKey: {
      type: String,
      required: false,
      default: null
    },
    stepToGo: {
      type: Number,
      required: false,
      default: 0
    },
    isLoadingPrevalidate: {
      type: Boolean,
      required: false,
      default: false
    },
    ...formValidationProps()
  },
  setup(props) {
    const captcha = ref(null);
    const {
      v$,
      getState,
      getErrorMessage,
      scrollToFirstError,
      checkValidation,
      states
    } = useFormValidation(props, captcha);

    return {
      carteMalin,
      carteCora,
      avantageMalin,
      illuEurocora,
      captcha,
      v$,
      getState,
      getErrorMessage,
      scrollToFirstError,
      checkValidation,
      states
    };
  },
  data() {
    return {
      step1: {
        email: "",
        emailConfirmation: "",
        password: "",
        password_confirmation: ""
      },
      step2: {
        civilite: "",
        nom: "",
        prenom: "",
        tel: "",
        optin_sms_suivi_commande_drive: false
      },
      step3: {
        adresse: "",
        complement_adresse: "",
        pays: "France",
        cp: "",
        ville: "",
        departement: ""
      },
      step4: {
        id_magasin: ""
      },
      step5: {
        optin_newsletter: ""
      },
      validatedSteps: {
        1: false,
        2: false,
        3: false,
        4: false,
        5: false
      },
      optin_newsletter_sms: "",
      captchaToken: null,
      carte2Visible: false,
      typeNumCarte2: "",
      cgvLinkCompteClient:
        "/legal/informations-juridiques/cgu-compte-client-internet",
      cgvLinkFid:
        "/legal/informations-juridiques/conditions-generales-fidelite",
      cgvLink: "",
      choixCarteFidelite: "",
      isCgvCompteClientChecked: false,
      nbEnfants: 1,
      captchaPrevalidateExecuted: false,
      captchaPrevalidateCarteExecuted: false
    };
  },
  computed: {
    otherMagasins() {
      const magasins = this.magasins.filter((magasin) => {
        return !this.magasinsByCp.some((mag) => mag.id === magasin.id);
      });
      if (!this.magasinId) {
        return magasins;
      }
      return magasins.filter((magasin) => {
        return parseInt(magasin.id) !== this.magasinId;
      });
    },
    currentMagasin() {
      return this.magasins.find((magasin) => {
        return parseInt(magasin.id) === this.magasinId;
      });
    },
    cp() {
      return this.step3.cp;
    },
    tel() {
      return this.step2.tel;
    },
    isSubmitButtonDisabled() {
      return (
        this.v$.step5.optin_newsletter.$invalid ||
        this.v$.optin_newsletter_sms.$invalid ||
        !this.isCgvCompteClientChecked
      );
    },
    isMagasinEligibleCarteDemat() {
      if (
        !useRuntimeConfig().public.CARTE_DEMAT_PHASE_PILOTE ||
        useRuntimeConfig().public.CARTE_DEMAT_MAGASINS_PILOTE.includes(
          +this.step4.id_magasin
        )
      ) {
        return true;
      }
      return false;
    }
  },
  validations() {
    return {
      step1: {
        email: {
          required,
          email
        },
        password: {
          required,
          password
        },
        emailConfirmation: {
          required,
          email,
          /* eslint-disable */

          equalsToEmail: function () {
            return this.step1.emailConfirmation === this.step1.email;
          }
        }
      },
      step2: {
        civilite: {
          required
        },
        nom: {
          required,
          minLength: minLength(2)
        },
        prenom: {
          required,
          minLength: minLength(2)
        },
        tel: {
          required,
          tel,
          hasNewsletterSms: function () {
            return this.optin_newsletter_sms ? telMobile(this.step2.tel) : true;
          }
        }
      },
      step3: {
        adresse: {
          required,
          minLength: minLength(3),
          maxLength: maxLength(355)
        },
        complement_adresse: {
          minLength: minLength(3),
          maxLength: maxLength(355)
        },
        cp: {
          required,
          zipCode
        },
        ville: {
          required
        },
        pays: {
          required
        },
        departement: {
          minLength: minLength(1),
          maxLength: maxLength(150)
        }
      },
      step4: {
        id_magasin: {
          required
        }
      },
      step5: {
        optin_newsletter: {
          required
        }
      },
      optin_newsletter_sms: {
        requiredIf: function (value) {
          if (this.choixCarteFidelite === "nouvelle-carte") {
            return value !== "";
          }
          return true;
        }
      }
    };
  },
  emits: ["save", "cp", "showAuth", "prevalidate", "prevalidateCarte"],
  methods: {
    verifyCaptcha(token) {
      if (token) {
        this.captchaToken = token;
      }
      this.step1.password_confirmation = this.step1.password;
      const model = {
        ...this.step1,
        ...this.step2,
        ...this.step3,
        ...this.step4,
        ...this.step5,
        "g-captcha-response": this.captchaToken
      };

      this.$emit("save", model);
    },
    verifyCaptchaPrevalidate(token) {
      if (token) {
        this.captchaPrevalidateExecuted = true;
        this.captchaToken = token;
      }
      this.$emit("prevalidate", {
        email: this.step1.email,
        civilite: this.step2.civilite,
        nom: this.step2.nom,
        prenom: this.step2.prenom,
        tel: this.step2.tel,
        "g-captcha-response": this.captchaToken
      });
    },
    save() {
      if (this.getActiveStep() < 5) {
        this.validateStep(this.getActiveStep());
      } else if (this.checkValidation(this.v$)) {
        if (this.captchaKey) {
          this.verifyCaptcha(this.captchaKey);
        } else {
          this.$refs.captcha.execute();
        }
      }
    },
    isStepActive(numStep) {
      return this.validatedSteps[numStep - 1];
    },
    isStepValid(numStep) {
      return this.v$[`step${numStep}`]
        ? !this.v$[`step${numStep}`].$invalid
        : null;
    },
    validateStep(numStep) {
      this.v$[`step${numStep}`].$touch();
      if (!this.v$[`step${numStep}`].$invalid) {
        if (numStep === 2) {
          if (this.captchaKey) {
            this.verifyCaptchaPrevalidate(this.captchaKey);
          } else {
            if (this.captchaPrevalidateExecuted) {
              this.$refs.captchaPrevalidate.reset();
            }
            this.$refs.captchaPrevalidate.execute();
          }
          return;
        }

        this.goNextStep(numStep);
      } else {
        this.scrollToFirstError();
      }
    },
    goNextStep(numStep) {
      this.validatedSteps[numStep] = true;
      if (this.$refs[`step${numStep + 1}`]) {
        this.$nextTick(() => {
          const el = this.$refs[`step${numStep + 1}`].$el;
          const top = getOffset(el).top;
          window.scrollTo(0, top - 100);
          this.$refs[`focus${numStep + 1}`].focus();
        });
      }
    },
    getActiveStep() {
      if (this.validatedSteps["5"] && !this.validatedSteps["6"]) {
        return 6;
      }
      if (this.validatedSteps["4"]) {
        return 5;
      }
      if (this.validatedSteps["3"]) {
        return 4;
      }
      if (this.validatedSteps["2"]) {
        return 3;
      }
      if (this.validatedSteps["1"]) {
        return 2;
      }
      return 1;
    },
    imageFormCarte(typeNumCarte) {
      if (!typeNumCarte) {
        return null;
      } else if (typeNumCarte === "cora") {
        return carteCora;
      } else {
        return carteMalin;
      }
    },
    showCgvCompteClientPopin() {
      this.cgvLink = this.cgvLinkCompteClient;
      this.$refs.cgvPopin.show();
    },
    showCgvFidPopin() {
      this.cgvLink = this.cgvLinkFid;
      this.$refs.cgvPopin.show();
    }
  },
  watch: {
    currentMagasin: {
      handler(value) {
        if (value) {
          this.step4.id_magasin = this.currentMagasin.id;
          this.v$.step4.id_magasin.$touch();
        }
      },
      immediate: true
    },
    tel: {
      handler(value) {
        if (value && telMobile(value)) {
          this.step2.optin_sms_suivi_commande_drive = true;
        } else {
          this.step2.optin_sms_suivi_commande_drive = false;
        }
      },
      immediate: true
    },
    cp: {
      handler() {
        if (this.getState(this.v$, "cp", "step3") === this.states.SUCCESS) {
          this.$emit("cp", this.cp);
        }
      },
      immediate: true
    },
    stepToGo: {
      handler() {
        if (this.stepToGo > 0) {
          this.goNextStep(this.stepToGo - 1);
        }
      }
    }
  }
};
</script>

<template>
  <Form
    class="c-inscription-form"
    :error="error"
    @submit.prevent="save()"
  >

    <BanniereInformation class="u-mb-lg u-none@md" />
    <GridRow class="u-flex u-flex-align-center u-flex-justify-between u-gap-sm">
      <GridCol>
        <h1 class="c-inscription-form__title u-mb-md@md">
          <span class="c-inscription-form__title-text">Créez votre compte</span>
        </h1>
        <p class="c-inscription-form__explanation">
          Vous pourrez vous connecter sur tous les sites et applications Cora.<br />
          Vous disposez déjà d’un compte cora.fr ou l’application ?
          <Button
            variant="primary"
            link
            @click="$emit('showAuth')"
          >
            Me connecter
          </Button>
        </p>
      </GridCol>
      <GridCol class="u-none@md">
        <MeaCoraproLogin
          class="c-inscription-form__mea-corapro"
          card-padding="u-p-sm"
        />
      </GridCol>
    </GridRow>

    <FormStep
      icon-name="lock"
      legend="Vos identifiants"
      ref="step1"
      title
      class="u-full-width"
    >
      <div class="c-inscription-form__step-content">
        <FormTip>
          <FormGroup
            :state="getState(v$,'email', 'step1')"
            class="c-inscription-form__form-group"
          >
            <FormInput
              ref="focus1"
              id="email"
              name="email"
              type="email"
              autocomplete
              autocapitalize="off"
              :state="getState(v$,'email', 'step1')"
              aria-describedby="emailMessage"
              @blur="v$.step1.email.$touch()"
              @input="v$.step1.email.$reset()"
              v-model.trim="step1.email"
            >
              Adresse email *
            </FormInput>
            <template #message>
              <div id="emailMessage">
                <p v-if="v$.step1.email.required.$invalid">
                  Merci de renseigner votre adresse e-mail
                </p>
                <p v-else>
                  {{ getErrorMessage("email","Le format de cette adresse e-mail est invalide") }}
                </p>
              </div>
            </template>
          </FormGroup>

          <FormGroup
            :state="getState(v$,'emailConfirmation', 'step1')"
            class="c-inscription-form__form-group"
          >
            <FormInput
              type="email"
              autocapitalize="off"
              autocomplete="off"
              :state="getState(v$,'emailConfirmation', 'step1')"
              aria-describedby="emailConfirmMessage"
              @blur="v$.step1.emailConfirmation.$touch()"
              @input="v$.step1.emailConfirmation.$reset()"
              v-model.trim="step1.emailConfirmation"
            >
              Confirmation adresse email *
            </FormInput>
            <template #message>
              <div id="emailConfirmMessage">
                <p v-if="v$.step1.emailConfirmation.required.$invalid">
                  Merci de confirmer votre adresse e-mail
                </p>
                <p v-else-if="v$.step1.emailConfirmation.email.$invalid">
                  Le format de cette adresse e-mail est invalide
                </p>
                <p v-else-if="v$.step1.emailConfirmation.equalsToEmail.$invalid">
                  L'adresse email n'est pas identique
                </p>
                <p v-else>
                  Le format de cette adresse e-mail est invalide
                </p>
              </div>
            </template>
          </FormGroup>
          <template #tip>
            <div
              id="emailTip"
              class="u-none@sm"
            >
              <p>
                Votre adresse email sera votre identifiant pour vous connecter chez Cora
              </p>
            </div>
          </template>
        </FormTip>

        <FormTip class="c-inscription-form__tip-password">
          <FormGroup
            :state="getState(v$,'password', 'step1')"
            class="c-inscription-form__form-group"
          >
            <FormInput
              type="password"
              autocomplete="off"
              autocapitalize="off"
              :state="getState(v$,'password', 'step1')"
              aria-describedby="passwordMessage"
              v-model.trim="step1.password"
              @blur="v$.step1.password.$touch()"
              @input="v$.step1.password.$reset()"
            >
              Mot de passe *
            </FormInput>
            <template #message>
              <div
                id="passwordMessage"
                class="u-none@sm"
              >
                <p v-if="v$.step1.password.required.$invalid">
                  Merci de renseigner votre mot de passe
                </p>
                <template v-else>
                  <p>Votre mot de passe est incorrect</p>
                  <p class="u-color-grey-1 u-font-12 u-m-xs">
                    Pour rappel votre mot de passe doit comporter au moins 8 caractères, dont 1 minuscule, 1 majuscule et 1 chiffre.
                  </p>
                </template>
              </div>
            </template>
          </FormGroup>
          <!-- <FormGroup class="c-inscription-form__form-group u-none@sm">
            <FormCheckbox><span class="u-font-14">Rester connecté(e)</span></FormCheckbox>
          </FormGroup> -->
          <template #tip>
            <div
              id="passwordTip"
              class="c-inscription-form__tip-password-content"
            >
              <p class="u-mr-lg u-mb-xs">
                Pour garantir la sécurité de votre compte, votre mot de passe doit contenir au minimum :
              </p>
              <PasswordRequirements :password="step1.password" />
            </div>
          </template>
        </FormTip>
        <template v-if="!validatedSteps['1']">
          <p class="c-inscription-form__required-field">
            Champs obligatoires*
          </p>
          <Button
            variant="secondary"
            radius="full"
            size="md"
            class="u-mt-md u-ml-lg u-flex-justify-center@sm"
            :disabled="!isStepValid(1)"
            @click="validateStep(1)"
          >
            Je valide mes identifiants
          </Button>
        </template>
      </div>
      <MeaCoraproLogin class="c-inscription-form__mea-corapro u-mt-lg u-mx-auto u-none u-block@md" />
    </FormStep>

    <FormStep
      icon-name="user"
      legend="Vos informations personnelles"
      ref="step2"
      title
      class="u-full-width"
      :disabled="!isStepActive(2)"
    >
      <div
        class="c-inscription-form__step-content"
        v-show="isStepActive(2)"
      >
        <label class="u-block u-font-13 u-mb-xs u-color-text-light">Civilité *</label>
        <FormGroup
          :state="getState(v$,'civilite', 'step2')"
          inline
          class="c-inscription-form__form-group"
        >
          <FormRadio
            ref="focus2"
            name="civilite"
            value="Mme"
            class="u-mr-lg"
            aria-describedby="civiliteMessage"
            v-model="step2.civilite"
          >
            Madame
          </FormRadio>
          <FormRadio
            name="civilite"
            value="M."
            aria-describedby="civiliteMessage"
            v-model="step2.civilite"
          >
            Monsieur
          </FormRadio>
          <template #message>
            <div id="civiliteMessage">
              <p>
                {{ getErrorMessage("civilite","Merci de renseigner votre civilité") }}
              </p>
            </div>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'nom', 'step2')"
          class="c-inscription-form__form-group"
        >
          <FormInput
            :state="getState(v$,'nom', 'step2')"
            id="nom"
            name="nom"
            maxlength="60"
            aria-describedby="nomMessage"
            @blur="v$.step2.nom.$touch()"
            @input="v$.step2.nom.$reset()"
            v-model.trim="step2.nom"
          >
            Nom *
          </FormInput>
          <template #message>
            <div id="nomMessage">
              <p v-if="v$.step2.nom.minLength.$invalid">
                {{ getErrorMessage("nom","Veuillez entrer un nom valide : compris entre 2 et 250 caractères maximum et au format texte") }}
              </p>
              <p v-else>
                {{ getErrorMessage("nom","Merci de renseigner votre nom") }}
              </p>
            </div>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'prenom', 'step2')"
          class="c-inscription-form__form-group"
        >
          <FormInput
            :state="getState(v$,'prenom', 'step2')"
            id="prenom"
            maxlength="40"
            aria-describedby="prenomMessage"
            @blur="v$.step2.prenom.$touch()"
            @input="v$.step2.prenom.$reset()"
            v-model.trim="step2.prenom"
          >
            Prénom *
          </FormInput>
          <template #message>
            <div id="prenomMessage">
              <p v-if="v$.step2.prenom.minLength.$invalid">
                {{ getErrorMessage("prenom","Veuillez entrer un prénom valide : compris entre 2 et 250 caractères maximum et au format texte") }}
              </p>
              <p v-else>
                {{ getErrorMessage("prenom","Merci de renseigner votre prénom") }}
              </p>

            </div>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'tel', 'step2')"
          class="c-inscription-form__form-group"
        >
          <FormInput
            :state="getState(v$,'tel', 'step2')"
            type="tel"
            name="tel"
            autocomplete="tel"
            aria-describedby="numeroMessage"
            @blur="v$.step2.tel.$touch()"
            @input="v$.step2.tel.$reset()"
            v-model.trim="step2.tel"
          >
            Téléphone *
          </FormInput>
          <template #message>
            <div id="numeroMessage">
              <p v-if="v$.step2.tel.required.$invalid">
                Merci de renseigner votre numéro de téléphone
              </p>
              <p v-else-if="v$.step2.tel.hasNewsletterSms.$invalid">
                Veuillez saisir un numéro de téléphone mobile pour recevoir les offres Cora par SMS
              </p>
              <p v-else>
                {{ getErrorMessage("tel","Veuillez entrer un numéro de téléphone valide qui contient entre 6 et 20 caractères, y compris les espaces") }}
              </p>
            </div>
          </template>
        </FormGroup>

        <template v-if="!validatedSteps['2']">
          <p class="c-inscription-form__required-field u-mt-lg u-mb-lg">
            Champs obligatoires*
          </p>

          <Captcha
            v-if="!captchaKey"
            ref="captchaPrevalidate"
            @verify="verifyCaptchaPrevalidate"
          />

          <LoadingSpinner
            v-if="isLoadingPrevalidate"
            class="u-pt-md"
            size="xs"
            message="Vérification de vos informations"
            row
          />
          <Button
            v-else
            variant="secondary"
            radius="full"
            size="md"
            class="u-mt-md u-ml-lg u-flex-justify-center@sm"
            :disabled="!isStepValid(2)"
            @click="validateStep(2)"
          >
            Je valide mes informations
          </Button>
        </template>
      </div>
    </FormStep>

    <FormStep
      icon-name="home"
      legend="Votre adresse de facturation"
      ref="step3"
      title
      class="u-full-width"
      :disabled="!isStepActive(3)"
    >
      <div
        class="c-inscription-form__step-content"
        v-show="isStepActive(3)"
      >
        <FormGroup
          :state="getState(v$,'adresse', 'step3')"
          class="c-inscription-form__form-group"
        >
          <FormInput
            :state="getState(v$,'adresse', 'step3')"
            id="adresse"
            aria-describedby="adresseMessage"
            ref="focus3"
            @blur="v$.step3.adresse.$touch()"
            @input="v$.step3.adresse.$reset()"
            v-model.trim="step3.adresse"
          >
            Adresse *
          </FormInput>
          <template #message>
            <div id="adresseMessage">
              <p v-if="v$.step3.adresse.required.$invalid">
                Merci de renseigner votre adresse
              </p>
              <p v-else>
                {{ getErrorMessage("adresse","Veuillez entrer une adresse valide : elle doit comporter entre 3 et 350 caractères") }}
              </p>
            </div>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'complement_adresse', 'step3')"
          class="c-inscription-form__form-group"
        >
          <FormInput
            v-model.trim="step3.complement_adresse"
            aria-describedby="complementAdresseMessage"
            @blur="v$.step3.complement_adresse.$touch()"
            @input="v$.step3.complement_adresse.$reset()"
            :state="getState(v$,'complement_adresse', 'step3')"
          >
            Complément
          </FormInput>
          <template #message>
            <div id="complementAdresseMessage">
              <p>
                {{ getErrorMessage("complement_adresse", "Veuillez entrer un complément d'adresse valide : il doit comporter entre 3 et 350 caractères") }}
              </p>
            </div>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'cp', 'step3')"
          class="c-inscription-form__form-group"
        >
          <FormInput
            :state="getState(v$,'cp', 'step3')"
            id="cp"
            type="tel"
            name="postal-code"
            autocomplete="postal-code"
            pattern="[0-9]*"
            size="xs"
            aria-describedby="codePostalMessage"
            @blur="v$.step3.cp.$touch()"
            @input="v$.step3.cp.$reset()"
            v-model="step3.cp"
          >
            Code Postal *
          </FormInput>
          <template #message>
            <div id="codePostalMessage">
              <p v-if="v$.step3.cp.required.$invalid">
                Merci de renseigner votre code postal
              </p>
              <p v-else>
                {{ getErrorMessage("cp","Veuillez entrer un code postal valide : il doit comporter entre 3 et 10 caractères") }}
              </p>
            </div>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'ville', 'step3')"
          class="c-inscription-form__form-group"
        >
          <FormInput
            :state="getState(v$,'ville', 'step3')"
            id="ville"
            aria-describedby="villeMessage"
            @blur="v$.step3.ville.$touch()"
            @input="v$.step3.ville.$reset()"
            v-model.trim="step3.ville"
          >
            Ville *
          </FormInput>
          <template #message>
            <div id="villeMessage">
              <p>
                {{ getErrorMessage("ville", "Merci de renseigner votre ville") }}
              </p>
            </div>
          </template>
        </Formgroup>
        <FormGroup
          :state="getState(v$,'pays', 'step3')"
          class="c-inscription-form__form-group"
        >
          <FormSelect
            v-model="step3.pays"
            title="Pays"
            :state="getState(v$,'pays', 'step3')"
            aria-describedby="paysMessage"
            id="pays"
          >
            <FormSelectOption value="France">
              France
            </FormSelectOption>
            <FormSelectOption value="Belgique">
              Belgique
            </FormSelectOption>
            <FormSelectOption value="Suisse">
              Suisse
            </FormSelectOption>
            <FormSelectOption value="Allemagne">
              Allemagne
            </FormSelectOption>
            <FormSelectOption value="Luxembourg">
              Luxembourg
            </FormSelectOption>
          </FormSelect>
          <template #message>
            <div id="paysMessage">
              <p>
                {{ getErrorMessage("pays") }}
              </p>
            </div>
          </template>
        </FormGroup>
        <FormGroup
          v-show="step3.pays !== 'France'"
          :state="getState(v$,'departement', 'step3')"
          class="c-inscription-form__form-group"
        >
          <FormInput
            :state="getState(v$,'departement', 'step3')"
            id="departement"
            aria-describedby="departementMessage"
            @blur="v$.step3.departement.$touch()"
            @input="v$.step3.departement.$reset()"
            v-model.trim="step3.departement"
          >
            Etat / Département
          </FormInput>
          <template #message>
            <div id="departementMessage">
              <p>
                {{ getErrorMessage("departement", "Veuillez entrer un Etat ou Département valide : il doit comporter entre 1 et 150 caractères") }}
              </p>
            </div>
          </template>
        </Formgroup>
        <template v-if="!validatedSteps['3']">
          <p class="c-inscription-form__required-field u-mt-lg u-mb-lg">
            * champs obligatoires
          </p>
          <Button
            variant="secondary"
            radius="full"
            size="md"
            class="u-mt-md u-ml-lg u-flex-justify-center@sm"
            :disabled="!isStepValid(3)"
            @click="validateStep(3)"
          >
            Je valide mon adresse
          </Button>
        </template>
      </div>
    </FormStep>

    <FormStep
      icon-name="shop"
      legend="Votre magasin"
      ref="step4"
      title
      class="u-full-width"
      :disabled="!isStepActive(4)"
    >
      <div
        class="c-inscription-form__step-content"
        v-show="isStepActive(4)"
      >
        <label for="selectMagasin">
          <h2 class="u-font-19 u-mb-xs">
            Sélectionnez le magasin près de chez vous !*
          </h2>
        </label>
        <FormGroup
          :state="getState(v$,'id_magasin', 'step4')"
          class="c-inscription-form__form-group"
        >
          <FormSelect
            id="selectMagasin"
            :state="getState(v$,'id_magasin', 'step4')"
            v-model="step4.id_magasin"
            aria-describedby="magasinMessage"
            ref="focus4"
            :defaultValue="currentMagasin ? null : 'Sélectionnez un magasin'"
          >
            <FormSelectOptGroup
              v-if="currentMagasin"
              label="Vous êtes rattaché(e) au magasin"
            >
              <FormSelectOption :value="currentMagasin.id">
                {{ currentMagasin.attributes.magasin }}
              </FormSelectOption>
            </FormSelectOptGroup>
            <FormSelectOptGroup
              v-if="magasinsByCp.length > 0"
              label="Les magasins proche de chez vous"
            >
              <FormSelectOption
                v-for="magasin in magasinsByCp"
                :key="magasin.id"
                :value="magasin.id"
              >
                {{ magasin.attributes.magasin }}
              </FormSelectOption>
            </FormSelectOptGroup>
            <FormSelectOptGroup label="Autres magasins">
              <FormSelectOption
                v-for="magasin in otherMagasins"
                :key="magasin.id"
                :value="magasin.id"
              >
                {{ magasin.attributes.magasin }}
              </FormSelectOption>
            </FormSelectOptGroup>
          </FormSelect>
          <template #message>
            <div aria-describedby="magasinMessage">
              <p>
                {{ getErrorMessage("id_magasin", "Merci de choisir un magasin") }}
              </p>
            </div>
          </template>
        </FormGroup>

        <template v-if="!validatedSteps['4']">
          <p class="c-inscription-form__required-field u-mt-lg u-mb-lg">
            * champs obligatoires
          </p>
          <Button
            variant="secondary"
            radius="full"
            size="md"
            class="u-mt-md u-ml-lg u-flex-justify-center@sm"
            :disabled="!isStepValid(4)"
            @click="validateStep(4)"
          >
            Je valide mon choix
          </Button>
        </template>
      </div>
    </FormStep>
    <FormStep
      icon-name="notification"
      legend="Vos notifications"
      ref="step5"
      title
      class="u-full-width"
      :disabled="!isStepActive(5)"
      :class="!isStepActive(5) && 'u-pb-lg'"
    >
      <div
        class="c-inscription-form__step-content"
        v-show="isStepActive(5)"
      >

        <h2 class="u-font-19 u-mb-md">
          Souhaitez-vous recevoir les promotions et les offres Cora&nbsp;x&nbsp;Carrefour ?<Button
            link
            class="u-ml-xs"
            to="#juridiqueNewsletter"
          >(¹)</Button>
        </h2>

        <div class="c-form-inscription__subcribe-promo">
          <span class="c-form-inscription__subcribe-promo-label">par email *</span>
          <FormGroup
            :state="getState(v$,'optin_newsletter', 'step5')"
            inline
          >
            <FormRadio
              ref="focus6"
              name="optin_newsletter"
              :value="true"
              class="c-form-inscription__subcribe-promo-input"
              aria-describedby="optinNewsLetterMailMessage"
              v-model="step5.optin_newsletter"
            >
              Oui
            </FormRadio>
            <FormRadio
              name="optin_newsletter"
              :value="false"
              class="c-form-inscription__subcribe-promo-input"
              aria-describedby="optinNewsLetterMailMessage"
              v-model="step5.optin_newsletter"
            >
              Non
            </FormRadio>
            <template #message>
              <div id="optinNewsLetterMailMessage">
                <p>
                  {{ getErrorMessage("optin_newsletter","Merci de renseigner votre choix") }}
                </p>
              </div>
            </template>
          </FormGroup>
        </div>

        <div
          v-if="choixCarteFidelite === 'nouvelle-carte'"
          class="c-form-inscription__subcribe-promo"
        >
          <span class="c-form-inscription__subcribe-promo-label">par SMS *</span>
          <FormGroup
            :state="getState(v$,'optin_newsletter_sms')"
            inline
          >
            <FormRadio
              name="optin_newsletter_sms"
              :value="true"
              class="c-form-inscription__subcribe-promo-input"
              aria-describedby="optinNewsLetterSms"
              v-model="optin_newsletter_sms"
            >
              Oui
            </FormRadio>
            <FormRadio
              name="optin_newsletter_sms"
              :value="false"
              class="c-form-inscription__subcribe-promo-input"
              aria-describedby="optinNewsLetterSms"
              v-model="optin_newsletter_sms"
            >
              Non
            </FormRadio>
            <template #message>
              <div id="optinNewsLetterSms">
                <p>
                  {{ getErrorMessage("optin_newsletter_sms","Merci de renseigner votre choix") }}
                </p>
              </div>
            </template>
          </FormGroup>
        </div>
      </div>
    </FormStep>
    <div
      v-if="isStepActive(5)"
      class="u-mt-lg"
    >
      <FormCheckbox
        class="u-flex-self-start u-px-md@md u-text-left"
        v-model="isCgvCompteClientChecked"
      >
        <p class="u-color-grey-1 u-font-14">
          En cochant cette case, vous acceptez les <Button
            link
            class="c-inscription-form__cgv-button"
            @click="showCgvCompteClientPopin()"
          >
            Conditions Générales d’Utilisation du Compte Client Internet
          </Button> *
        </p>
      </FormCheckbox>
      <Captcha
        v-if="!captchaKey"
        ref="captcha"
        @verify="verifyCaptcha"
      />
      <div class="u-mt-lg20 u-px-xlg u-pl-md@lg u-ml-xlg u-ml-lg@md">
        <Button
          variant="primary"
          radius="full"
          type="submit"
          value="Je valide mon compte"
          size="md"
          :disabled="(state === 'loading' || isSubmitButtonDisabled)"
          class="u-mt-sm u-ml-lg u-flex-justify-center@sm"
        />
      </div>
      <p class="c-inscription-form__required-field u-mt-lg30 u-mb-lg">
        * champs obligatoires
      </p>
    </div>
    <MentionsLegalesCompte class="c-inscription-form__notice u-mt-lg u-pb-lg" />
    <MentionsLegalesNewsletter
      class="c-inscription-form__notice u-pb-lg u-anchor-has-sticky"
      id="juridiqueNewsletter"
      info="¹"
    />
    <MentionsLegalesFidelite class="c-inscription-form__notice u-pb-lg" />
    <Popin
      ref="cgvPopin"
      aria-label="Conditions générales de vente"
      size="lg"
    >
      <CmsFetcher :link="cgvLink" />
      <div class="u-flex u-flex-justify-center">
        <Button
          variant="secondary"
          size="md"
          radius="full"
          class="u-m-sm"
          @click="$refs.cgvPopin.hide()"
        >
          OK
        </Button>
      </div>
    </Popin>
    <Popin
      ref="carteAssocieePopin"
      aria-label="Aide"
      size="lg"
    >
      <CmsFetcher link="/infos-et-services/pages-cachees/carte-fidelite-associee-a-un-compte-comment-faire" />
      <div class="u-flex u-flex-justify-center">
        <Button
          variant="secondary"
          size="md"
          radius="full"
          class="u-m-sm"
          @click="$refs.carteAssocieePopin.hide()"
        >
          OK
        </Button>
      </div>
    </Popin>
  </Form>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/inscription-form.scss";
</style>
